import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { cloneDeep, isNil } from 'lodash';
import moment from 'moment-timezone';
import { firstValueFrom } from 'rxjs';
import { skip } from 'rxjs/operators';

import {
  ConversationConfig,
  ConversationConfigSchema,
  ConversationRequestConfigure,
  ConversationRequestConfigureSchema,
  IdentityConfigConversation,
  IdentityConfigConversationCaptureTarget,
  OrgData,
  UserData,
} from '@pwp-common';

import { getFieldControlValueOrDefault, getFieldValuesFromFormInComponent } from '../../../common/objects/form-helper';
import { URLValidator } from '../../../common/validators/url-validator/url-validator';
import { ConversationConfigService } from '../../../services/conversation/conversation-config/conversation-config.service';
import { ConversationEndpointService } from '../../../services/conversation/conversation-endpoint/conversation-endpoint.service';
import { OrgDataService } from '../../../services/orgs/org-data/org-data.service';
import { UserDataService } from '../../../services/user/user-data/user-data.service';
import { EditAvailableHoursByDowInput } from '../../core/edit-available-hours-by-dow/edit-available-hours-by-dow-input';
import { calleeEntitiesToEditorOutput } from '../../core/workers-autocomplete/editor-output/callee-entities/callee-entities-to-editor-output/callee-entities-to-editor-output';
import { editorOutputToCalleeEntities } from '../../core/workers-autocomplete/editor-output/callee-entities/editor-output-to-callee-entities/editor-output-to-callee-entities';
import { makeAuditEntry } from '../../generic/settings/common/audit-entry/helpers/make-audit-entry';
import { AuditEntry } from '../../generic/settings/common/audit-entry/interfaces';

@UntilDestroy()
@Component({
  selector: 'app-conversation-config-editor',
  templateUrl: './conversation-config-editor.component.html',
  styleUrls: ['./conversation-config-editor.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationConfigEditorComponent implements OnInit {
  private readonly captureIdentityToVendorEnabledFieldName = 'captureIdentityToVendorEnabled';

  private conversationConfig: ConversationConfig;

  private orgData: OrgData;

  private userData: Map<string, UserData>;

  public readonly form = new UntypedFormGroup({
    inactiveAfterDuration: new UntypedFormControl(),
    closedAfterDuration: new UntypedFormControl(),
    pIIRetentionDuration: new UntypedFormControl(),
    redirectURL: new UntypedFormControl('', URLValidator.url()),
    waitingRoomConfigEnabled: new UntypedFormControl(),
    captureIdentityToVendorEnabled: new UntypedFormControl(),
    communicationWorkflowId: new UntypedFormControl(),
    serviceProviders: new UntypedFormControl([]),
  });

  public loading = true;

  public auditEntry: AuditEntry;

  public availableTimesEditorInput: EditAvailableHoursByDowInput;

  public spamDetectionEnabled = false;

  ////////////////////////////////////////////////////////////////////////////
  // Constructor
  ////////////////////////////////////////////////////////////////////////////
  constructor(
    private conversationConfigService: ConversationConfigService,
    private conversationEndpointService: ConversationEndpointService,
    private orgDataService: OrgDataService,
    private userDataService: UserDataService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ////////////////////////////////////////////////////////////////////////////
  // Lifecycle
  ////////////////////////////////////////////////////////////////////////////

  public ngOnInit(): void {
    this.refreshData().catch((error) =>
      console.error('ConversationConfigEditorComponent: failed to refresh data', error),
    );

    this.conversationConfigService
      .getUniqueDoc({ query: [], takeOne: false })
      .pipe(skip(1), untilDestroyed(this))
      .subscribe((conversationConfig) => {
        if (isNil(conversationConfig)) {
          return;
        }
        this.conversationConfig = conversationConfig;
        this.setSpamDetectionEnabled();
        this.changeDetectorRef.detectChanges();
      });
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Define Form
  /////////////////////////////////////////////////////////////////////////////////////////////

  public updateUI = () => {
    if (isNil(this.orgData) || isNil(this.conversationConfig) || isNil(this.userData)) {
      return;
    }
    this.loading = true;
    this.availableTimesEditorInput = {
      timezone: this.orgData.getTimezone(),
      timeSelection: this.conversationConfig.getAvailableTimes(),
    };

    const serviceProvidersOutput = calleeEntitiesToEditorOutput(
      this.conversationConfig.getServiceProviders() || [],
      this.userData,
      this.orgData,
    );

    const formValues = {
      [ConversationRequestConfigureSchema.inactiveAfterDuration]: moment
        .duration(this.conversationConfig.getInactiveAfterDuration())
        .asMinutes(),
      [ConversationRequestConfigureSchema.closedAfterDuration]: moment
        .duration(this.conversationConfig.getClosedAfterDuration())
        .asMinutes(),
      [ConversationRequestConfigureSchema.pIIRetentionDuration]: moment
        .duration(this.conversationConfig.getPIIRetentionDuration())
        .asHours(),
      [ConversationRequestConfigureSchema.redirectURL]:
        this.conversationConfig.getRedirectURL() ?? '',
      [ConversationRequestConfigureSchema.waitingRoomConfigEnabled]:
        this.conversationConfig.getWaitingRoomConfig().getEnabled() ?? false,
      [ConversationRequestConfigureSchema.communicationWorkflowId]:
        this.conversationConfig.getCommunicationWorkflowId(),
      [this.captureIdentityToVendorEnabledFieldName]:
        this.conversationConfig.getIdentityConfig().getCaptureTarget() ===
        IdentityConfigConversationCaptureTarget.vendor,
      [ConversationRequestConfigureSchema.serviceProviders]: serviceProvidersOutput,
    };

    this.form.patchValue(formValues);
    this.setSpamDetectionEnabled();

    this.auditEntry = makeAuditEntry(this.conversationConfig);
    this.loading = false;
    this.changeDetectorRef.detectChanges();
  };

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Get Data
  /////////////////////////////////////////////////////////////////////////////////////////////

  public refreshData = async () => {
    this.loading = true;
    return Promise.all([
      firstValueFrom(this.orgDataService.getOrgData()),
      firstValueFrom(this.conversationConfigService.getUniqueDoc({ query: [] })),
      firstValueFrom(this.userDataService.getDocs()),
    ]).then(async (result) => {
      this.orgData = result[0];
      this.conversationConfig = result[1];
      this.userData = result[2];
      this.updateUI();
    });
  };

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Do Upload
  /////////////////////////////////////////////////////////////////////////////////////////////

  public upload = async () => {
    const defaults = {} as any;
    defaults[ConversationRequestConfigureSchema.waitingRoomConfigEnabled] = false;
    defaults[ConversationRequestConfigureSchema.redirectURL] = ConversationRequestConfigureSchema.Defaults.redirectURL;

    const parameters = getFieldValuesFromFormInComponent(
      undefined,
      [
        ConversationRequestConfigureSchema.waitingRoomConfigEnabled,
        ConversationRequestConfigureSchema.redirectURL,
        ConversationRequestConfigureSchema.communicationWorkflowId,
      ],
      defaults,
      this.form.controls,
    );

    const captureIdentityToVendorEnabled = getFieldControlValueOrDefault(this.captureIdentityToVendorEnabled, false);

    if (parameters[ConversationRequestConfigureSchema.redirectURL] === '') {
      parameters[ConversationRequestConfigureSchema.redirectURL] =
        ConversationRequestConfigureSchema.Defaults.redirectURL;
    }

    let identityConfig = cloneDeep(ConversationConfigSchema.Defaults.identityConfig);
    if (captureIdentityToVendorEnabled === true) {
      identityConfig = new IdentityConfigConversation({
        captureTarget: IdentityConfigConversationCaptureTarget.vendor,
      });
    }

    const inactiveAfterDuration = moment
      .duration(this.form.value[ConversationConfigSchema.inactiveAfterDuration], 'minutes')
      .toISOString();
    const closedAfterDuration = moment
      .duration(this.form.value[ConversationConfigSchema.closedAfterDuration], 'minutes')
      .toISOString();
    const pIIRetentionDuration = moment
      .duration(this.form.value[ConversationConfigSchema.pIIRetentionDuration], 'hours')
      .toISOString();

    const serviceProviders = editorOutputToCalleeEntities(
      this.form.value[ConversationRequestConfigureSchema.serviceProviders] || [],
    );

    const uploadObj = new ConversationRequestConfigure({
      ...parameters,
      type: 'configure',
      inactiveAfterDuration,
      closedAfterDuration,
      pIIRetentionDuration,
      availableTimes: this.availableTimesEditorInput.timeSelection,
      conversationConfigId: this.conversationConfig.getId(),
      identityConfig,
      serviceProviders,
    });

    await this.conversationEndpointService.configure(uploadObj);
  };

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Is Valid
  /////////////////////////////////////////////////////////////////////////////////////////////

  public isValid = (): boolean => this.form.valid;

  ///////////////////////////////////////////////////////////////////////////////////////////
  // Form: Getters
  ///////////////////////////////////////////////////////////////////////////////////////////
  public get inactiveAfterDuration() {
    return this.form.get(ConversationRequestConfigureSchema.inactiveAfterDuration) as UntypedFormControl;
  }

  public get closedAfterDuration() {
    return this.form.get(ConversationRequestConfigureSchema.closedAfterDuration) as UntypedFormControl;
  }

  public get pIIRetentionDuration() {
    return this.form.get(ConversationRequestConfigureSchema.pIIRetentionDuration) as UntypedFormControl;
  }

  public get redirectURL() {
    return this.form.get(ConversationRequestConfigureSchema.redirectURL) as UntypedFormControl;
  }

  public get waitingRoomConfigEnabled() {
    return this.form.get(ConversationRequestConfigureSchema.waitingRoomConfigEnabled) as UntypedFormControl;
  }

  public get captureIdentityToVendorEnabled() {
    return this.form.get(this.captureIdentityToVendorEnabledFieldName) as UntypedFormControl;
  }

  public get communicationWorkflowId() {
    return this.form.get('communicationWorkflowId') as UntypedFormControl;
  }

  public get serviceProviders() {
    return this.form.get(ConversationRequestConfigureSchema.serviceProviders) as UntypedFormControl;
  }

  ///////////////////////////////////////////////////////////////////////////////////////////
  // Private
  ///////////////////////////////////////////////////////////////////////////////////////////

  private setSpamDetectionEnabled(): void {
    this.spamDetectionEnabled = this.conversationConfig.getSpamDetectionConfig()?.getEnabled() ?? false;
    if (this.spamDetectionEnabled) {
      this.waitingRoomConfigEnabled.disable({ emitEvent: false });
    } else {
      this.waitingRoomConfigEnabled.enable({ emitEvent: false });
    }
  }
}
